import { useState } from 'react'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { BillingToggle, FaqList, PlanCTA, SliceWedge, planFeatures, priceFor, priceNote } from './shared'

const MAX_SLICES = 16

/**
  * Pick a tier on the left; the right panel shows what you
 * get, with the month's slices drawn as wedges so the tiers feel different.
 */
export default function PricingPlans({ ctx }) {
  const { plans, billing, setBilling, isAnnual } = ctx
  const [selectedTier, setSelectedTier] = useState(() => (plans.find((p) => p.featured) ?? plans[0]).tier)
  const plan = plans.find((p) => p.tier === selectedTier)

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:py-20">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
        {/* Left: pitch + tier picker */}
        <div>
          <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl">
            Watch everything.
            <br />
            <span className="text-muted-foreground">Choose how much you give.</span>
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
            Every plan unlocks the full library of independent films and series. What changes is
            how many slices you get to give to the creators you love.
          </p>

          <BillingToggle billing={billing} setBilling={setBilling} className="mt-8" />

          <div role="radiogroup" aria-label="Choose a plan" className="mt-6 space-y-3">
            {plans.map((p) => {
              const active = p.tier === selectedTier
              return (
                <button
                  key={p.tier}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setSelectedTier(p.tier)}
                  className={`group flex w-full items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all ${
                    active
                      ? 'border-primary bg-primary/10 shadow-[0_0_0_1px_hsl(var(--primary)/0.5)]'
                      : 'border-border bg-card/40 hover:border-primary/40 hover:bg-card'
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                      active ? 'border-primary' : 'border-muted-foreground/40'
                    }`}
                  >
                    {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                  </span>
                  <span className="flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{p.tier}</span>
                      {p.featured && (
                        <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                          Most popular
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-sm text-amber-400/90">{p.slices} slices a month</span>
                  </span>
                  <span className="text-right tabular-nums">
                    <span className="text-xl font-bold text-foreground">${priceFor(p, isAnnual)}</span>
                    <span className="text-sm text-muted-foreground">/{isAnnual ? 'yr' : 'mo'}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right: selected plan */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-6 sm:p-8">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-amber-400/10 blur-3xl"
            />

            <div className="relative flex items-end justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{plan.tier}</p>
                <p className="mt-1 flex items-baseline gap-1 tabular-nums">
                  <span className="text-5xl font-bold tracking-tight text-foreground sm:text-6xl">
                    ${priceFor(plan, isAnnual)}
                  </span>
                  <span className="text-muted-foreground">/{isAnnual ? 'year' : 'month'}</span>
                </p>
                <p className="mt-1 text-sm text-muted-foreground tabular-nums">{priceNote(plan, isAnnual)}</p>
              </div>
            </div>

            {/* Slices visual */}
            <div className="relative mt-8 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] p-5">
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-medium text-foreground">Your slices each month</p>
                <p className="text-2xl font-bold tabular-nums text-amber-400">{plan.slices}</p>
              </div>
              <div className="mt-4 grid grid-cols-8 gap-2">
                {Array.from({ length: MAX_SLICES }, (_, i) => {
                  const filled = i < plan.slices
                  return (
                    <motion.span
                      key={i}
                      initial={false}
                      animate={{ opacity: filled ? 1 : 0.18, scale: filled ? 1 : 0.8 }}
                      transition={{ duration: 0.25, delay: filled ? i * 0.025 : 0 }}
                      className="flex aspect-square items-center justify-center"
                    >
                      <SliceWedge className={`h-full w-full ${filled ? 'text-amber-400' : 'text-muted-foreground'}`} />
                    </motion.span>
                  )
                })}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Give them to the films and creators you want more of. Anything you don't give joins the
                Community Fund, which subscribers vote on each month.
              </p>
            </div>

            <ul className="relative mt-6 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
              {planFeatures(plan).map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 text-sm text-foreground/90">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {feature}
                </li>
              ))}
            </ul>

            <PlanCTA plan={plan} ctx={ctx} variant="default" className="relative mt-8 h-12 w-full text-base" />
            <p className="relative mt-3 text-center text-xs text-muted-foreground">
              Cancel anytime · Manage or switch plans from your account
            </p>
          </div>
        </div>
      </div>

      <section className="mt-24 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_2fr]">
        <h2 className="text-2xl font-semibold text-foreground">Questions</h2>
        <FaqList />
      </section>
    </div>
  )
}
